import React, {useEffect, useRef, useState} from 'react'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {pxToRem} from '../../utills/pxToRem'
import useSitePreference from '../../hooks/use-site-preference'
import PropTypes from 'prop-types'

const GoogleMaps = ({customLat, customLong, storeList, zoom}) => {
    const mapRef = useRef(null)
    const [scriptLoaded, setScriptLoaded] = useState(false)
    const {app: appConfig} = getConfig()
    const apiKey = appConfig.googleAPI.googelAPIKey
    const {data} = useSitePreference('defaultStoreLatLong')

    useEffect(() => {
        if (!apiKey) {
            console.error('Google Maps API key missing')
            return
        }

        if (window.google && window.google.maps) {
            setScriptLoaded(true)
            return
        }

        if (!document.getElementById('google-maps-script')) {
            const callbackName = `initGoogleMaps_${Date.now()}`
            window[callbackName] = () => {
                console.log('Google Maps API ready')
                setScriptLoaded(true)
                delete window[callbackName]
            }

            const script = document.createElement('script')
            script.id = 'google-maps-script'
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${callbackName}`
            script.async = true
            script.defer = true
            script.onerror = (e) => {
                console.error('Failed to load Google Maps script', e)
                delete window[callbackName]
            }
            document.body.appendChild(script)
        } else {
            if (window.google && window.google.maps) {
                setScriptLoaded(true)
            } else {
                const checkGoogleMaps = () => {
                    if (window.google && window.google.maps) {
                        setScriptLoaded(true)
                    } else {
                        setTimeout(checkGoogleMaps, 100)
                    }
                }
                checkGoogleMaps()
            }
        }
    }, [apiKey])

    useEffect(() => {
        if (!scriptLoaded || !data?.defaultStoreLatLong || !storeList?.length) return

        let parsedLatLng
        try {
            parsedLatLng = JSON.parse(data.defaultStoreLatLong)
        } catch (error) {
            console.error('Failed to parse lat/lng JSON string:', error)
            return
        }

        const position = {
            lat: customLat ? customLat : parseFloat(parsedLatLng.lat),
            lng: customLong ? customLong : parseFloat(parsedLatLng.lng)
        }

        const map = new window.google.maps.Map(mapRef.current, {
            center: position,
            zoom: zoom || 12,
            mapTypeControl: false,
            streetViewControl: false,
            scrollwheel: true
        })

        let currentInfoWindow = null
        const bounds = new window.google.maps.LatLngBounds()

        storeList.forEach((store) => {
            const storePosition = {
                lat: parseFloat(store.latitude),
                lng: parseFloat(store.longitude)
            }

            const marker = new window.google.maps.Marker({
                position: storePosition,
                map,
                title: store.name,
                animation: window.google.maps.Animation.DROP
            })

            const infoWindow = new window.google.maps.InfoWindow({
                content: `
                <div>
                <style>
                    #storeLink {
                        color: #000;
                        line-height: normal;
                        font-weight: 300;
                        font-size: 13px;
                        text-decoration: none;
                        font-family: Montserrat;
                        letter-spacing: 1px;
                    }
                    #storeLink:focus {
                        outline: 2px solid #2460CC !important;
                        outline-offset: -2px;
                        text-decoration: none;
                        color: #aa0924;
                    }
                </style>
                <a id="storeLink" href="/storedetails/?StoreID=${store.id}" target="_blank">
                    ${store.address1}, ${store.city}, ${store.stateCode}, ${store.countryCode}, ${store.postalCode}
                </a>
                </div>
            `
            })

            marker.addListener('click', () => {
                if (currentInfoWindow && currentInfoWindow !== infoWindow) {
                    currentInfoWindow.close()
                }
                infoWindow.open(map, marker)
                currentInfoWindow = infoWindow
                setTimeout(() => {
                    const link = document.getElementById('storeLink')
                    if (link) link.focus()
                }, 100)
            })

            bounds.extend(storePosition)
        })

        if (storeList.length > 1) {
            map.fitBounds(bounds)
        } else {
            map.setCenter(position)
            map.setZoom(zoom || 12)
        }
    }, [scriptLoaded, storeList, data, customLat, customLong, zoom])

    return (
        <Box>
            <Box ref={mapRef} style={{width: '100%', height: '800px'}}></Box>
        </Box>
    )
}

export default GoogleMaps

GoogleMaps.propTypes = {
    customLat: PropTypes.number,
    customLong: PropTypes.number,
    zoom: PropTypes.number,
    storeList: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
            latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            address1: PropTypes.string,
            city: PropTypes.string,
            stateCode: PropTypes.string,
            countryCode: PropTypes.string,
            postalCode: PropTypes.string
        })
    )
}
